import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  EVisitItem,
  ICreateEVisitRuleParams,
  IEventsDaysTypes,
  IEVisitEvents,
  IEVisitsRules,
  IEVisitsState,
  IGetEventsParams,
  IGetEVisitRulesParams,
  IGetOfficeHoursParams,
  IOfficeHours,
  IOfficeHoursDeleteParams,
  IOfficeHoursWeekRule,
  IOfficeHoursWeekRuleWithId,
  TDeleteEVisitRuleParams,
  TRulesManipulationError,
  TUpdateEVisitRuleParams,
} from '../../types/e-visits';
import { TUserMedicalInstitution } from '../../types/users';
import { ITagSelectOption } from '@hilbi/web-common/components/e-visits/select/options/types';

const initialState: IEVisitsState = {
  selectedInstitution: undefined,
  selectedDate: new Date(),
  tagOptions: [],
  selectedTag: null,
};

const eVisitsSlice = createSlice({
  name: 'eVisits',
  initialState,
  reducers: {
    /**
     * Get events for calendar
     */
    getEventsRequest: (state, _: PayloadAction<IGetEventsParams>) => {
      state.getEVisitsEventsStatus = 'pending';
    },
    getTagsRequest: (state) => {
      state.getTagsRequestStatus = 'pending';
    },
    getEventSuccess: (state, action: PayloadAction<IEVisitEvents>) => {
      state.getEVisitsEventsStatus = 'success';
      state.events = action.payload;
    },
    getEventError: (state) => {
      state.getEVisitsEventsStatus = 'error';
      state.events = undefined;
    },
    getEventReset: (state) => {
      state.getEVisitsEventsStatus = undefined;
      state.events = undefined;
    },
    /**
     * Get events types for calendar
     */
    getEventsTypesForDayRequest: (state, _: PayloadAction<IGetEventsParams>) => {
      state.getEventsTypesForDayStatus = 'pending';
    },
    getEventsTypesForDaySuccess: (state, action: PayloadAction<IEventsDaysTypes>) => {
      state.getEventsTypesForDayStatus = 'success';
      state.eventsTypes = action.payload;
    },
    getEventsTypesForDayError: (state) => {
      state.getEventsTypesForDayStatus = 'error';
      state.eventsTypes = undefined;
    },
    getEventsTypesForDayReset: (state) => {
      state.getEventsTypesForDayStatus = undefined;
      state.eventsTypes = undefined;
    },
    /**
     * Get office hours
     */
    getOfficeHoursRequest: (state, _: PayloadAction<IGetOfficeHoursParams>) => {
      state.getOfficeHoursStatus = 'pending';
    },
    getOfficeHoursSuccess: (state, action: PayloadAction<IOfficeHours>) => {
      state.getOfficeHoursStatus = 'success';
      state.officeHours = action.payload;
    },
    getOfficeHoursError: (state) => {
      state.getOfficeHoursStatus = 'error';
      state.officeHours = undefined;
    },
    getOfficeHoursReset: (state) => {
      state.getOfficeHoursStatus = undefined;
      state.officeHours = undefined;
    },

    /**
     * Cancel eVisit order
     */
    cancelEVisitRequest: (
      state,
      _: PayloadAction<{
        orderId: string;
        reason: string;
      }>,
    ) => {
      state.cancelEVisitStatus = 'pending';
    },
    cancelEVisitSuccess: (state) => {
      state.cancelEVisitStatus = 'success';
    },
    cancelEVisitError: (state) => {
      state.cancelEVisitStatus = 'error';
    },
    cancelEVisitReset: (state) => {
      state.cancelEVisitStatus = undefined;
    },

    /**
     * Get eVisits for today for dashboard
     */
    getFeedEVisitsRequest: (state) => {
      state.getFeedEVisitsStatus = 'pending';
    },
    getFeedEVisitsSuccess: (state, action: PayloadAction<IEVisitEvents>) => {
      state.getFeedEVisitsStatus = 'success';
      const dayKey = Object.keys(action.payload)[0];

      if (dayKey) {
        state.feedEvents = action.payload[dayKey];
      }
    },
    getFeedEVisitsError: (state) => {
      state.getFeedEVisitsStatus = 'error';
    },
    getFeedEVisitsReset: (state) => {
      state.getFeedEVisitsStatus = undefined;
      state.feedEvents = undefined;
    },

    /**
     * Get rules for selected dates
     */
    getEVisitsRulesRequest: (state, _: PayloadAction<IGetEVisitRulesParams>) => {
      state.getEVisitRuleStatus = 'pending';
    },
    getEVisitsRulesSuccess: (state, action: PayloadAction<IEVisitsRules>) => {
      state.getEVisitRuleStatus = 'success';
      state.eVisitsRules = action.payload;
    },
    getEVisitsRulesError: (state) => {
      state.getEVisitRuleStatus = 'error';
    },
    getEVisitsRulesReset: (state) => {
      state.getEVisitRuleStatus = undefined;
    },

    /**
     * create e-visit rule actions
     */
    createEVisitRuleRequest: (state, _: PayloadAction<ICreateEVisitRuleParams>) => {
      state.createEVisitRuleStatus = 'pending';
      state.createOfficeRuleError = undefined;
    },
    createEVisitRuleSuccess: (state) => {
      state.createEVisitRuleStatus = 'success';
    },
    createEVisitRuleError: (state, action: PayloadAction<TRulesManipulationError | undefined>) => {
      state.createEVisitRuleStatus = 'error';
      state.creatEVisitRuleError = action.payload;
    },
    createEVisitRuleReset: (state) => {
      state.createEVisitRuleStatus = undefined;
      state.creatEVisitRuleError = undefined;
    },

    /**
     * Update e-visit rule actions
     */
    updateEVisitRuleRequest: (state, _: PayloadAction<TUpdateEVisitRuleParams>) => {
      state.updateEVisitRuleStatus = 'pending';
      state.updateOfficeRuleError = undefined;
    },
    updateEVisitRuleSuccess: (state) => {
      state.updateEVisitRuleStatus = 'success';
    },
    updateEVisitRuleError: (state, action: PayloadAction<TRulesManipulationError | undefined>) => {
      state.updateEVisitRuleStatus = 'error';
      state.updateEVisitRuleError = action.payload;
    },
    updateEVisitRuleReset: (state) => {
      state.updateEVisitRuleStatus = undefined;
      state.createOfficeRuleError = undefined;
    },

    /**
     * Delete e-visit rule actions
     */
    deleteEVisitRuleRequest: (state, _: PayloadAction<TDeleteEVisitRuleParams>) => {
      state.deleteEVisitRuleStatus = 'pending';
      state.deleteOfficeRuleError = undefined;
    },
    deleteEVisitRuleSuccess: (state) => {
      state.deleteEVisitRuleStatus = 'success';
    },
    deleteEVisitRuleError: (state, action: PayloadAction<TRulesManipulationError | undefined>) => {
      state.deleteEVisitRuleStatus = 'error';
      state.deleteEVisitRuleError = action.payload;
    },
    deleteEVisitRuleReset: (state) => {
      state.deleteEVisitRuleStatus = undefined;
      state.deleteOfficeRuleError = undefined;
    },

    /**
     * Get rules for office hours
     */
    getOfficeRulesRequest: (
      state,
      _: PayloadAction<{ expertiseCode: string; medicalInstitutionId: string }>,
    ) => {
      state.getOfficeRuleStatus = 'pending';
    },
    getOfficeRulesSuccess: (state, action: PayloadAction<IOfficeHoursWeekRuleWithId[]>) => {
      state.getOfficeRuleStatus = 'success';
      state.officeHoursRules = action.payload;
    },
    getOfficeRulesError: (state) => {
      state.getOfficeRuleStatus = 'error';
    },
    getOfficeRulesReset: (state) => {
      state.getOfficeRuleStatus = undefined;
      state.officeHoursRules = [];
    },

    /**
     * create office rule actions
     */
    createOfficeRuleRequest: (state, _: PayloadAction<IOfficeHoursWeekRule>) => {
      state.createOfficeRuleStatus = 'pending';
      state.createOfficeRuleError = undefined;
    },
    createOfficeRuleSuccess: (state) => {
      state.createOfficeRuleStatus = 'success';
    },
    createOfficeRuleError: (state, action: PayloadAction<TRulesManipulationError | undefined>) => {
      state.createOfficeRuleStatus = 'error';
      state.createOfficeRuleError = action.payload;
    },
    createOfficeRuleReset: (state) => {
      state.createOfficeRuleStatus = undefined;
      state.createOfficeRuleError = undefined;
    },

    /**
     * Update office rule actions
     */
    updateOfficeRuleRequest: (state, _: PayloadAction<IOfficeHoursWeekRuleWithId>) => {
      state.updateOfficeRuleStatus = 'pending';
      state.updateOfficeRuleError = undefined;
    },
    updateOfficeRuleSuccess: (state) => {
      state.updateOfficeRuleStatus = 'success';
    },
    updateOfficeRuleError: (state, action: PayloadAction<TRulesManipulationError | undefined>) => {
      state.updateOfficeRuleStatus = 'error';
      state.updateOfficeRuleError = action.payload;
    },
    updateOfficeRuleReset: (state) => {
      state.updateOfficeRuleStatus = undefined;
      state.createOfficeRuleError = undefined;
    },

    /**
     * Delete office rule actions
     */
    deleteOfficeRuleRequest: (state, _: PayloadAction<IOfficeHoursDeleteParams>) => {
      state.deleteOfficeRuleStatus = 'pending';
      state.deleteOfficeRuleError = undefined;
    },
    deleteOfficeRuleSuccess: (state) => {
      state.deleteOfficeRuleStatus = 'success';
    },
    deleteOfficeRuleError: (state, action: PayloadAction<TRulesManipulationError | undefined>) => {
      state.deleteOfficeRuleStatus = 'error';
      state.deleteOfficeRuleError = action.payload;
    },
    deleteOfficeRuleReset: (state) => {
      state.deleteOfficeRuleStatus = undefined;
      state.deleteOfficeRuleError = undefined;
    },
    setSelectedInstitution: (
      state: IEVisitsState,
      action: PayloadAction<TUserMedicalInstitution | undefined>,
    ) => {
      state.selectedInstitution = action.payload;
    },
    setSelectedDate: (state: IEVisitsState, action: PayloadAction<Date>) => {
      state.selectedDate = action.payload;
    },
    setTagOptions: (state: IEVisitsState, action: PayloadAction<ITagSelectOption[]>) => {
      state.tagOptions = action.payload;
    },
    setSelectedTag: (state: IEVisitsState, action: PayloadAction<ITagSelectOption | null>) => {
      state.selectedTag = action.payload;
    },

    setSingleEvent: (state: IEVisitsState, action: PayloadAction<EVisitItem | undefined>) => {
      state.singleEvisitItem = action.payload;
    },

    clearTag: (state: IEVisitsState) => {
      state.selectedTag = null;
    },
  },
});

export const { actions, name, reducer } = eVisitsSlice;
export default reducer;
